/**
 * JSON serialization support for ImageSource and related types
 */

import { decodeBase64 } from '@utils';
import { z } from 'zod';
import { CanvasKitImageSource } from './CanvasKitImageSource';
import { ImageOriginSchema } from './ImageOriginJSON';
import type { ImageSource } from './ImageSource';
import { MemoryImageSource } from './MemoryImageSource';
import { SVGImageSource } from './SVGImageSource';
import { canvasKit } from '@resources';

/**
 * Schema for base64-encoded binary data that transforms to Uint8Array
 */
const DataSchema = z.string().transform(str => decodeBase64(str));

/**
 * Base schema for all ImageSource types
 */
const ImageSourceBaseSchema = z.object({
  id: z.string(),
  origin: ImageOriginSchema.optional()
});

/**
 * Schema for MemoryImageSource JSON representation
 */
const MemoryImageSourceDataSchema = ImageSourceBaseSchema.extend({
  type: z.literal('memory'),
  data: DataSchema
});

/**
 * Schema for SVGImageSource JSON representation
 */
const SVGImageSourceDataSchema = ImageSourceBaseSchema.extend({
  type: z.literal('svg'),
  svgContent: z.string()
});

/**
 * Schema for CanvasKitImageSource JSON representation
 */
const CanvasKitImageSourceDataSchema = ImageSourceBaseSchema.extend({
  type: z.literal('canvaskit'),
  imageData: DataSchema
});

/**
 * Discriminated union schema for all ImageSource types
 */
export const ImageSourceSchema = z.discriminatedUnion('type', [
  MemoryImageSourceDataSchema,
  SVGImageSourceDataSchema,
  CanvasKitImageSourceDataSchema
]);

/**
 * Type for ImageSource JSON data
 */
export type ImageSourceData = z.infer<typeof ImageSourceSchema>;

/**
 * Convert an ImageSource instance to JSON
 */
export function imageSourceToJSON(imageSource: ImageSource): unknown {
  // Use the built-in toJSON method if available
  if ('toJSON' in imageSource && typeof imageSource.toJSON === 'function') {
    const toJSON = imageSource.toJSON as () => unknown;
    const json = toJSON.call(imageSource);
    return json;
  }

  throw new Error(
    `ImageSource type does not support JSON serialization: ${imageSource.constructor.name}`
  );
}

/**
 * Reconstruct an ImageSource from JSON data
 */
export function imageSourceFromJSON(data: unknown): ImageSource {
  const parsed = ImageSourceSchema.parse(data);

  switch (parsed.type) {
    case 'memory': {
      return new MemoryImageSource(parsed.data, parsed.id, parsed.origin);
    }

    case 'svg': {
      return new SVGImageSource(parsed.svgContent, parsed.id, parsed.origin);
    }

    case 'canvaskit': {
      // For CanvasKit sources, we need to decode the image data first
      const ckImage = canvasKit.MakeImageFromEncoded(parsed.imageData);
      if (!ckImage) {
        throw new Error('Failed to decode image data for CanvasKitImageSource');
      }
      return new CanvasKitImageSource(ckImage, parsed.id, parsed.origin);
    }

    default: {
      throw new Error(`Unknown ImageSource type: ${(parsed as { type: string }).type}`);
    }
  }
}
